import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTour, setTourStep } from '../lib/supplierTour';

// Supplier portal onboarding tour – a spotlight coachmark walkthrough that
// spans two routes (the portal home and /portal/compliance).
//
// The portal dims, one area is ringed in teal at a time, and a tip card
// explains it (step counter, title, body, Back / Next / Skip). The card
// repositions beside whatever's highlighted (flips above/below, clamps so
// it never clips the viewport); centre steps render the card mid-screen.
// Replay lives in the account menu (top-right); one tour step points at it.
//
// Cross-page mechanics:
//   - The current step index lives in the shared tour store (lib/supplierTour)
//     so it survives the route change that unmounts one page and mounts the
//     next. Both portal pages render this overlay; only the mounted one is
//     visible at a time.
//   - Each step names the route it belongs to. When the active step's route
//     isn't the page we're on, the overlay shows just the dim and asks the
//     host to navigate (onNavigate); the destination page then takes over.
//   - `ready` tells us the host page has finished loading. While a freshly
//     navigated page is still loading we hold the dim and wait; once ready, a
//     target that still isn't in the DOM is genuinely absent for this supplier
//     (e.g. a brand-new supplier with no brief card) and we skip past it in
//     the direction of travel.
//
// Teal accent (#2E8FA1) throughout – the same hue as the ⓘ help system the
// last step hands off to. Supplier-facing rules apply: professional, no
// mascots, British English. Copy lives in lib/supplierTour (SUPPLIER_TOUR_STEPS)
// so the component stays purely mechanical.
//
// Behaviour / a11y:
//   - Focus moves into the card each step; Tab is trapped within the card.
//   - Esc skips, Enter advances, Back/Next are real buttons.
//   - The target is scrolled into view before it's highlighted.
//   - prefers-reduced-motion removes the spotlight's position transition.
//   - On narrow screens the card renders as a bottom sheet.
//
// Props:
//   steps        the full ordered step list (SUPPLIER_TOUR_STEPS)
//   currentPath  the host page's pathname (useLocation().pathname)
//   ready        bool – host page has loaded; anchors exist if they're going to
//   onNavigate(route)  push a new route to carry the tour to another page
//   onClose()    fired on finish OR skip; the caller persists seen + completed_at

// The dim behind the tour. 0.85 buried the page — a supplier could barely make
// out the thing each step was pointing AT, which defeats a spotlight. 0.6 still
// reads unmistakably as "the rest is on hold" while leaving the surrounding
// layout legible enough to give the highlighted element context.
const DIM = 'rgba(7, 8, 6, 0.6)';
const TEAL = '#2E8FA1';
const CARD_W = 320;
const CARD_H_EST = 230; // used only for desktop above/below clamping
const GAP = 14;
const MARGIN = 12;

export default function SupplierTour({ steps, currentPath, ready = true, onNavigate, onClose, tipsEnabled = true, onTipsToggle, infoTipsOn = true, onInfoTipsToggle }) {
  const { step: idx } = useTour();
  // Final-step choices. `showTips` = replay this guided tour on future logins
  // (onboarding_tips_enabled). `infoTips` = keep the ⓘ info tips visible around
  // the portal (the same setting the account menu controls) – surfaced here
  // because the tour just introduced the ⓘ feature (account brief §6).
  const [showTips, setShowTips] = useState(tipsEnabled);
  const [infoTips, setInfoTips] = useState(infoTipsOn);
  const [rect, setRect] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const cardRef = useRef(null);
  const reducedMotion = useRef(false);
  const dirRef = useRef('next');     // last travel direction, for auto-skip
  const focusedForRef = useRef(-1);  // idx we've already moved focus into

  const step = steps[idx] || null;
  const isFinal = !!step && (step.final || idx === steps.length - 1);
  // Are we on the page this step lives on? Steps without a route are treated
  // as belonging to whatever page is hosting (defensive – all real steps set one).
  const onRoute = !step || !step.route || step.route === currentPath;

  // Environment probes – reduced-motion + narrow-screen, kept live.
  useEffect(() => {
    reducedMotion.current = window.matchMedia
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false;
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia('(max-width: 640px)');
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  const finish = useCallback(() => {
    if (onClose) onClose();
  }, [onClose]);

  const goTo = useCallback((i) => {
    if (i > steps.length - 1) { finish(); return; }
    setTourStep(Math.max(0, i));
  }, [steps.length, finish]);

  const next = useCallback(() => { dirRef.current = 'next'; goTo(idx + 1); }, [idx, goTo]);
  const back = useCallback(() => { dirRef.current = 'back'; goTo(Math.max(0, idx - 1)); }, [idx, goTo]);
  const skip = useCallback(() => finish(), [finish]);

  // Carry the tour to the step's route when we're not already on it. The
  // navigation unmounts this host and mounts the destination page, whose own
  // overlay picks up at the same step.
  useEffect(() => {
    if (step && step.route && step.route !== currentPath && onNavigate) {
      onNavigate(step.route);
    }
  }, [step, currentPath, onNavigate]);

  const updateRect = useCallback(() => {
    if (!step || !onRoute || !step.target) { setRect(null); return; }
    const el = document.querySelector(step.target);
    if (!el) { setRect(null); return; }
    const r = el.getBoundingClientRect();
    setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
  }, [step, onRoute]);

  // On step change (and once the page is ready): scroll the target into view
  // and measure it. Missing-but-ready target → skip in travel direction.
  useEffect(() => {
    if (!step || !onRoute) return undefined;
    if (!step.target) { setRect(null); return undefined; } // centre step
    const el = document.querySelector(step.target);
    if (el) {
      if (el.scrollIntoView) el.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'auto' });
      updateRect();
      return undefined;
    }
    // Target not in the DOM. If the page hasn't loaded yet, wait – this effect
    // re-runs when `ready` flips. If it's loaded, the target is genuinely
    // absent for this supplier, so skip past it.
    setRect(null);
    if (!ready) return undefined;
    const t = setTimeout(() => {
      if (dirRef.current === 'back') goTo(idx - 1);
      else goTo(idx + 1);
    }, 0);
    return () => clearTimeout(t);
  }, [step, onRoute, ready, updateRect, idx, goTo]);

  // Keep the spotlight glued to the target through scroll / resize.
  useEffect(() => {
    if (!step || !onRoute || !step.target) return undefined;
    const onChange = () => updateRect();
    window.addEventListener('scroll', onChange, true);
    window.addEventListener('resize', onChange);
    return () => {
      window.removeEventListener('scroll', onChange, true);
      window.removeEventListener('resize', onChange);
    };
  }, [step, onRoute, updateRect]);

  // The card is shown once we're on the right page and either have a measured
  // target or it's a centre step. Off-route / still-loading shows dim only.
  const showCard = !!step && onRoute && (rect != null || !step.target);

  // Move focus into the card the first time it appears for a given step.
  useEffect(() => {
    if (!showCard) return undefined;
    if (focusedForRef.current === idx) return undefined;
    focusedForRef.current = idx;
    const t = setTimeout(() => {
      const card = cardRef.current;
      if (!card) return;
      const nextBtn = card.querySelector('[data-tour-next]');
      (nextBtn || card).focus();
    }, 0);
    return () => clearTimeout(t);
  }, [showCard, idx]);

  // Keyboard: Esc skips always; Enter advances + Tab traps only when a card
  // is on screen (so keys don't fire while a page is mid-load).
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); skip(); return; }
      if (!showCard) return;
      if (e.key === 'Enter') { e.preventDefault(); next(); return; }
      if (e.key === 'Tab') {
        const card = cardRef.current;
        if (!card) return;
        const focusable = card.querySelectorAll('button, [tabindex]:not([tabindex="-1"])');
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [skip, next, showCard]);

  if (!step) return null;

  // Card placement: bottom sheet on mobile; centred for no-target steps;
  // otherwise beside the target (prefer below, flip above when short on
  // space) with horizontal clamping so it never clips.
  let cardStyle;
  if (isMobile) {
    cardStyle = { position: 'fixed', left: 0, right: 0, bottom: 0, width: '100%', borderRadius: '16px 16px 0 0' };
  } else if (!rect) {
    cardStyle = { position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: CARD_W };
  } else {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const below = rect.top + rect.height + GAP;
    const placeAbove = below + CARD_H_EST > vh;
    let top = placeAbove ? rect.top - CARD_H_EST - GAP : below;
    top = Math.max(MARGIN, Math.min(top, vh - CARD_H_EST - MARGIN));
    let left = Math.min(Math.max(MARGIN, rect.left), vw - CARD_W - MARGIN);
    cardStyle = { position: 'fixed', top, left, width: CARD_W };
  }

  return (
    <div className="fixed inset-0 z-[9999]" role="dialog" aria-modal="true" aria-label="Portal tour">
      {/* Backdrop blocks page interaction. For anchored steps the spotlight's
          box-shadow paints the dim, so the backdrop stays transparent to
          avoid doubling up; otherwise (centre step, or mid-navigation / page
          still loading) the backdrop carries the dim itself. */}
      <div className="absolute inset-0" style={{ background: rect ? 'transparent' : DIM }} />

      {/* Spotlight – a transparent box at the target with a huge box-shadow
          spread that dims everything around it, plus the teal ring. */}
      {rect && onRoute && (
        <div
          aria-hidden="true"
          style={{
            position: 'fixed',
            top: rect.top - 6,
            left: rect.left - 6,
            width: rect.width + 12,
            height: rect.height + 12,
            borderRadius: 10,
            boxShadow: `0 0 0 9999px ${DIM}, 0 0 0 2px ${TEAL}, 0 0 0 7px rgba(46, 143, 161, 0.22)`,
            pointerEvents: 'none',
            transition: reducedMotion.current ? 'none' : 'top 0.2s ease, left 0.2s ease, width 0.2s ease, height 0.2s ease',
          }}
        />
      )}

      {/* Tip card – only once we're on the right page with something to show. */}
      {showCard && (
        <div
          ref={cardRef}
          tabIndex={-1}
          style={cardStyle}
          className="bg-white border border-[#D4D9C6] shadow-[0_14px_40px_rgba(0,0,0,0.18)] p-[18px] outline-none rounded-xl"
        >
          <p className="font-['Metropolis'] text-[10px] font-extrabold tracking-[0.18em] uppercase text-[#2E8FA1]">
            Step {idx + 1} of {steps.length}
          </p>
          <h4 className="font-['Metropolis'] text-base font-extrabold text-ink mt-1.5 mb-1.5">{step.title}</h4>
          <p className="font-['Metropolis'] text-[13px] text-fusion-mid-grey leading-relaxed m-0">{step.body}</p>

          {isFinal && (
            <div className="mt-3.5 flex flex-col gap-2">
              {/* Keep the ⓘ info tips on – the same choice the account menu
                  offers permanently, surfaced here at the relevant moment (§6). */}
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={infoTips}
                  onChange={(e) => { setInfoTips(e.target.checked); if (onInfoTipsToggle) onInfoTipsToggle(e.target.checked); }}
                  className="w-4 h-4 accent-[#2E8FA1] flex-shrink-0"
                />
                <span className="font-['Metropolis'] text-[12px] text-fusion-mid-grey">Keep the ⓘ info tips on around the portal</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={showTips}
                  onChange={(e) => { setShowTips(e.target.checked); if (onTipsToggle) onTipsToggle(e.target.checked); }}
                  className="w-4 h-4 accent-[#2E8FA1] flex-shrink-0"
                />
                <span className="font-['Metropolis'] text-[12px] text-fusion-mid-grey">Show this guided tour when I sign in</span>
              </label>
            </div>
          )}

          <div className="flex items-center justify-between gap-2.5 mt-4">
            <button
              type="button"
              onClick={skip}
              className="font-['Metropolis'] text-xs text-[#6c6f64] hover:text-ink bg-transparent border-0 cursor-pointer transition-colors"
            >
              Skip tour
            </button>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={back}
                disabled={idx === 0}
                className="font-['Metropolis'] text-[13px] font-bold px-3.5 py-2 rounded-md bg-transparent border border-[#D4D9C6] text-ink cursor-pointer transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Back
              </button>
              <button
                type="button"
                data-tour-next="true"
                onClick={next}
                className="font-['Metropolis'] text-[13px] font-bold px-3.5 py-2 rounded-md border-0 bg-fusion-green text-[#14210A] cursor-pointer hover:brightness-95 transition-[filter]"
              >
                {isFinal ? 'Done' : 'Next'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
